import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";

const FILTERS = ["pending", "approved", "rejected", "all"];
const KIND = { goldly: "Goldly Recharge", boost: "Boost", lead_gen: "Lead Generation", other: "Other (QR)" };

function PaymentRow({ p, onChange }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); onChange(); } catch (e) { alert(formatErr(e)); } finally { setBusy(false); } };
  const approve = () => {
    let body = {};
    if (p.kind === "other") {
      const g = window.prompt("Goldly to credit for this payment (leave 0 if none):", "0");
      if (g === null) return;
      if (!/^\d+$/.test(g.trim())) { alert("Enter a whole number (0 or more)"); return; }
      body = { goldly_amount: parseInt(g, 10) };
    } else if (!window.confirm(`Approve ₹${p.amount_inr} from @${p.username}? (${p.title})`)) return;
    run(() => api.post(`/admin/payments/${p.id}/approve`, body));
  };
  const reject = () => {
    const reason = window.prompt("Reason for rejecting (shown to user):", "Payment not found");
    if (reason === null) return;
    run(() => api.post(`/admin/payments/${p.id}/reject`, { reason }));
  };
  return (
    <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-sm" data-testid={`admin-payment-${p.id}`}>
      <div className="flex items-start gap-3">
        {p.screenshot_url && <a href={p.screenshot_url} target="_blank" rel="noreferrer"><img src={p.screenshot_url} alt="proof" className="w-14 h-14 rounded-lg object-cover"/></a>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-[#FFD700]">₹{p.amount_inr.toLocaleString("en-IN")}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10">{KIND[p.kind]}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 uppercase">{p.method}</span>
            <span className="text-[10px] font-bold uppercase text-white/60">{p.status}</span>
          </div>
          <div className="text-xs mt-0.5">{p.title}</div>
          <div className="text-[11px] text-white/60">@{p.username} · UTR <b className="font-mono text-white">{p.utr}</b> · {new Date(p.created_at).toLocaleString()}</div>
          {p.reject_reason && <div className="text-[11px] text-[#FF2A5F]">Rejected: {p.reject_reason}</div>}
        </div>
      </div>
      {p.status === "pending" && (
        <div className="flex gap-2 mt-2">
          <button disabled={busy} onClick={approve} data-testid={`admin-payment-approve-${p.id}`} className="px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold">Approve & Activate</button>
          <button disabled={busy} onClick={reject} data-testid={`admin-payment-reject-${p.id}`} className="px-3 py-1.5 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/40 text-[#FF2A5F] text-xs font-bold">Reject</button>
        </div>
      )}
    </div>
  );
}

function RazorpayStatus() {
  const [st, setSt] = useState(null);
  const check = () => { setSt("loading"); api.get("/admin/rzp-status").then(r => setSt(r.data)).catch(() => setSt({ error: "Check failed" })); };
  useEffect(check, []);
  if (!st || st === "loading") return <p className="text-xs text-white/50">Checking Razorpay keys…</p>;
  return (
    <div className={`p-3 rounded-xl border text-xs space-y-1 ${st.auth_ok ? "bg-emerald-500/10 border-emerald-500/30" : "bg-[#FF2A5F]/10 border-[#FF2A5F]/30"}`} data-testid="admin-rzp-status">
      <div className="font-bold text-sm">{st.auth_ok ? "✅ Razorpay keys working" : "❌ Razorpay keys not working"}</div>
      <div>Key ID type: <b>{st.key_id_mode}</b> · Key ID length: <b>{st.key_id_length}</b> (should be 23) · Secret length: <b>{st.secret_length}</b> (usually 24)</div>
      {st.error && <div className="text-[#FF2A5F]">Razorpay says: {st.error}</div>}
      <button onClick={check} data-testid="admin-rzp-recheck" className="mt-1 px-3 py-1 rounded-full bg-white/10 font-bold">Check again</button>
    </div>
  );
}

function RazorpayList() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/rzp-payments").then(r => setRows(r.data)).catch(() => {}); }, []);
  const total = rows.reduce((a, r) => a + (r.amount_inr || 0), 0);
  return (
    <div className="space-y-2" data-testid="admin-rzp-payments">
      <div className="flex items-center justify-between">
        <h3 className="font-outfit font-bold">Razorpay payments (auto-credited)</h3>
        <span className="text-xs text-[#FFD700] font-bold" data-testid="admin-rzp-total">{rows.length} · ₹{total.toLocaleString("en-IN")}</span>
      </div>
      {rows.length === 0 ? <p className="text-sm text-white/50">No Razorpay payments yet.</p> : rows.map(r => (
        <div key={r.id} className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs" data-testid={`admin-rzp-${r.id}`}>
          <div className="flex justify-between"><b className="text-[#FFD700]">₹{r.amount_inr.toLocaleString("en-IN")}</b><span className="text-emerald-300 font-bold">PAID</span></div>
          <div>{r.title}</div>
          <div className="text-white/55">@{r.username} · {r.payment_id} · {new Date(r.paid_at).toLocaleString()}</div>
          {r.result && <div className="text-white/70">{r.result}</div>}
        </div>
      ))}
    </div>
  );
}

export default function AdminPayments() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/admin/payments?status=${status}`).then(r => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="admin-payments">
      <RazorpayStatus/>
      <RazorpayList/>
      <h3 className="font-outfit font-bold pt-4">Old manual requests (link / QR)</h3>
      <p className="text-xs text-white/60">Only earlier link/QR requests appear here. Verify the UTR in Razorpay Dashboard before approving.</p>
      <div className="flex gap-2">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setStatus(f)} data-testid={`admin-payments-filter-${f}`}
            className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${status === f ? "bg-[#00E5FF] text-black" : "bg-white/10"}`}>{f}</button>
        ))}
      </div>
      {rows.length === 0 ? <p className="text-sm text-white/50">No {status} payments.</p> : rows.map(p => <PaymentRow key={p.id} p={p} onChange={load}/>)}
    </div>
  );
}
